import { useCallback, useRef } from 'react';

/**
 * Hook para debounce de funções, útil para otimizar performance de eventos de clique
 * @param callback - Função a ser executada após o delay
 * @param delay - Tempo de delay em milissegundos (padrão: 300ms)
 * @returns Função debounced
 */
export function useDebounce<T extends (...args: unknown[]) => void>(
  callback: T,
  delay: number = 300
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
}

/**
 * Hook para throttle de funções, útil para limitar a frequência de execução
 * @param callback - Função a ser executada
 * @param delay - Tempo mínimo entre execuções em milissegundos (padrão: 100ms)
 * @returns Função throttled
 */
export function useThrottle<T extends (...args: unknown[]) => void>(
  callback: T,
  delay: number = 100
): (...args: Parameters<T>) => void {
  const lastExecuted = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now();
      
      if (now - lastExecuted.current >= delay) {
        lastExecuted.current = now;
        callback(...args);
      } else {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        timeoutRef.current = setTimeout(() => {
          lastExecuted.current = Date.now();
          callback(...args);
        }, delay - (now - lastExecuted.current));
      }
    },
    [callback, delay]
  );
}